import type { MacroGraphSource, MacroDefinition } from './source';
import { GraphAttributeValidator } from './attributeSchema';
import { junctionGateAttributesSchema } from './gateAttributes';

export interface GraphSourceIssue { message: string; path?: (string | number)[] }

/** Validates references without projecting a graph into the legacy macro tree. */
export class GraphSourceRules {
  public validate(source: MacroGraphSource): GraphSourceIssue[] {
    const errors: GraphSourceIssue[] = [];
    const fail = (message: string): void => { errors.push({ message }); };
    for (const values of [source.modules, source.nodes, source.powershellScripts, source.junctions, source.edges]) {
      if (new Set(values.map(value => value.id)).size !== values.length) fail('Duplicate immutable identifier');
    }
    const versions = source.definitions.map(definition => `${definition.id}:${definition.revision}`);
    if (new Set(versions).size !== versions.length) fail('Duplicate definition revision');
    const modules = new Map(source.modules.map(module => [module.id, module]));
    const scripts = new Set(source.powershellScripts.map(script => script.id));
    for (const module of source.modules) {
      if (module.dependencies.some(id => !modules.has(id))) fail(`Missing module dependency: ${module.id}`);
    }
    if (this.hasCycle(source.modules.map(module => [module.id, module.dependencies]))) fail('Cyclic module dependency');
    for (const node of source.nodes) {
      const module = modules.get(node.moduleId);
      if (!module) fail(`Missing JavaScript module: ${node.id}`);
      else new GraphAttributeValidator().validate(node.attributes, module.attributesSchema).forEach(fail);
      if (new Set(node.powershellAttachments.map(attachment => attachment.attachmentId)).size !== node.powershellAttachments.length) fail('Duplicate attachment');
      if (node.powershellAttachments.some(attachment => !scripts.has(attachment.scriptId))) fail('Missing PowerShell attachment script');
    }
    for (const definition of source.definitions) this.validateDefinition(source, definition, fail);
    this.validateJunctions(source, fail);
    return errors;
  }

  private validateDefinition(source: MacroGraphSource, definition: MacroDefinition, fail: (message: string) => void): void {
    const steps = new Map(definition.steps.map(step => [step.id, step]));
    if (steps.size !== definition.steps.length) fail('Duplicate step identifier');
    const entry = steps.get(definition.entryStepId);
    if (entry?.kind !== 'node' || !source.nodes.some(node => node.id === entry.nodeId && node.role === 'entry')) fail('Macro requires a JavaScript entry prerequisite node');
    const nodeLinks: [string, string[]][] = [];
    for (const step of definition.steps) {
      const targets = step.kind === 'node' ? step.routes.map(route => route.target) : [step.next];
      if (targets.some(target => target.kind === 'step' && !steps.has(target.stepId))) fail('Missing flow target');
      if (step.kind === 'node') {
        if (!source.nodes.some(node => node.id === step.nodeId && node.role !== 'junction')) fail('Missing local JavaScript node');
        if (new Set(step.routes.map(route => route.id)).size !== step.routes.length) fail('Duplicate node route');
        nodeLinks.push([step.id, targets.flatMap(target => target.kind === 'step' && steps.get(target.stepId)?.kind === 'node' ? [target.stepId] : [])]);
      } else {
        if ((step.gate.kind === 'regions') !== (step.regions.length > 0)) fail('Gate regions must agree with gate kind');
        if (step.input && step.input.fromUs >= step.input.toUs) fail('Input slice must be a nonempty half-open interval');
        const packages = [...step.regions.map(region => region.packageId), ...(step.input ? [step.input.packageId] : [])];
        if (packages.some(id => !source.recordingPins.some(pin => pin.packageId === id))) fail('Missing recording pin');
      }
    }
    if (this.hasCycle(nodeLinks)) fail('Synchronous node-only cycle');
  }

  private validateJunctions(source: MacroGraphSource, fail: (message: string) => void): void {
    const junctions = new Map(source.junctions.map(junction => [junction.id, junction]));
    if (source.entryJunctionIds.some(id => !junctions.has(id))) fail('Missing entry junction');
    for (const edge of source.edges) {
      if (!junctions.has(edge.fromJunctionId) || !junctions.has(edge.toJunctionId)) fail('Missing edge junction');
      if (!source.definitions.some(definition => definition.id === edge.definitionId)) fail('Missing edge definition');
    }
    for (const junction of source.junctions) {
      const node = source.nodes.find(candidate => candidate.id === junction.nodeId);
      if (!node || node.role !== 'junction') { fail('Junction requires a JavaScript junction node'); continue; }
      const actual = source.edges.filter(edge => edge.fromJunctionId === junction.id).map(edge => edge.id).sort();
      if (JSON.stringify(actual) !== JSON.stringify([...junction.outgoingEdgeIds].sort())) fail('Junction outgoing routes disagree with edges');
      const gate = node.attributes["gate"];
      if (!gate || typeof gate !== 'object' || Array.isArray(gate)) { fail('Junction requires epoch gate attributes'); continue; }
      if (!junctionGateAttributesSchema.safeParse(gate).success) fail('Invalid junction gate declaration');
      const incoming = source.edges.filter(edge => edge.toJunctionId === junction.id).map(edge => edge.id).sort();
      if (!Array.isArray(gate["predecessorEdgeIds"]) || JSON.stringify(incoming) !== JSON.stringify([...gate["predecessorEdgeIds"]].sort())) fail('Gate membership disagrees with incoming edges');
      if (gate["factScope"] !== 'correlation-epoch' || gate["consume"] !== 'once-per-epoch' || !['inherit', 'new'].includes(String(gate["epochOnRelease"]))) fail('Invalid gate epoch policy');
      const template = source.modules.find(module => module.id === node.moduleId)?.template?.kind;
      if ((template === 'XOR' || template === 'NOT') && !gate["close"]) fail('XOR/NOT require explicit closure');
    }
    const sameEpoch: [string, string[]][] = source.junctions.map(junction => {
      const gate = source.nodes.find(node => node.id === junction.nodeId)?.attributes["gate"];
      const resets = gate && typeof gate === 'object' && !Array.isArray(gate) && gate["epochOnRelease"] === 'new';
      return [junction.id, resets ? [] : source.edges.filter(edge => edge.fromJunctionId === junction.id).map(edge => edge.toJunctionId)];
    });
    if (this.hasCycle(sameEpoch)) fail('Composition cycle must cross an epoch reset');
  }

  private hasCycle(links: [string, string[]][]): boolean {
    const remaining = new Map(links.map(([id, targets]) => [id, new Set(targets)]));
    while (remaining.size) {
      const leaves = [...remaining].filter(([, targets]) => ![...targets].some(id => remaining.has(id))).map(([id]) => id);
      if (!leaves.length) return true;
      leaves.forEach(id => remaining.delete(id));
    }
    return false;
  }
}
